import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { RootState } from 'store/index';
import {
  fetchPaymentSettings,
  createPaymentSetting,
  updatePaymentSetting,
  deletePaymentSetting,
} from 'store/paymentSettingsSlice';
import {
  PaymentSetting,
  CreatePaymentSettingDto,
  UpdatePaymentSettingDto,
} from 'types/paymentSetting';
import { commonGridStyles } from 'styles/gridStyles';
import ConfirmDeleteModal from 'components/modals/ConfirmDeleteModal';
import CreateEditPaymentSettingModal from './CreateEditPaymentSettingModal';
import styles from './Payments.module.scss';

interface PaymentSettingsTableProps {
  siteId: string | number;
}

const PaymentSettingsTable: React.FC<PaymentSettingsTableProps> = ({ siteId }) => {
  const dispatch = useAppDispatch();
  const { data, isLoading } = useAppSelector((state: RootState) => state.paymentSettings);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState<PaymentSetting | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [deleteModal, setDeleteModal] = useState<{ open: boolean; id: number | ''; title: string }>(
    {
      open: false,
      id: '',
      title: '',
    },
  );

  useEffect(() => {
    if (siteId) {
      dispatch(fetchPaymentSettings(siteId));
    }
  }, [dispatch, siteId]);

  const handleOpenCreateModal = () => {
    setError(null);
    setEditingSetting(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (setting: PaymentSetting) => {
    setError(null);
    setEditingSetting(setting);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSetting(null);
  };

  const handleSaveSetting = async (formData: CreatePaymentSettingDto | UpdatePaymentSettingDto) => {
    try {
      if (editingSetting) {
        await dispatch(
          updatePaymentSetting({
            id: editingSetting.id,
            data: formData as UpdatePaymentSettingDto,
          }),
        ).unwrap();
      } else {
        await dispatch(
          createPaymentSetting({ ...formData, siteId } as CreatePaymentSettingDto),
        ).unwrap();
      }
      handleCloseModal();
    } catch (err: any) {
      setError(typeof err === 'string' ? err : err.message || 'Failed to save setting');
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteModal.id !== '') {
      try {
        await dispatch(deletePaymentSetting(deleteModal.id as number)).unwrap();
        setDeleteModal({ open: false, id: '', title: '' });
      } catch (error) {
        console.error('Failed to delete setting:', error);
      }
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'type',
      headerName: 'Type',
      width: 80,
      renderCell: (params) => {
        const isDeposit = params.value?.toString().toLowerCase() === 'deposit';
        return (
          <Typography
            sx={{
              color: isDeposit ? '#2a8039' : '#ff4d4f',
              fontWeight: 600,
              fontSize: '13px',
              textTransform: 'capitalize',
              mt: '14px',
            }}
          >
            {params.value}
          </Typography>
        );
      },
    },
    { field: 'paymentCode', headerName: 'Payment Code', width: 150 },
    { field: 'callbackSecret', headerName: 'Call. Secret', flex: 1, minWidth: 200 },
    { field: 'apiBaseUrl', headerName: 'Base URL', flex: 1, minWidth: 200 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      sortable: false,
      renderCell: (params) => (
        <Box className={styles.actions} sx={{ display: 'flex', gap: '8px', mt: '8px' }}>
          <Button
            variant="contained"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditModal(params.row);
            }}
            sx={{ minWidth: '40px', padding: '4px' }}
          >
            <Edit size={16} />
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteModal({ open: true, id: params.row.id, title: params.row.paymentCode });
            }}
            sx={{ minWidth: '40px', padding: '4px' }}
          >
            <Trash2 size={16} />
          </Button>
        </Box>
      ),
    },
  ];

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          p: 4,
          height: '400px',
          alignItems: 'center',
        }}
      >
        <CircularProgress size={40} sx={{ color: '#00b3f5' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={handleOpenCreateModal}
          // The payments tab uses accent blue generically, mapped internally.
          sx={{ backgroundColor: '#00b3f5', '&:hover': { backgroundColor: '#0090c5' } }}
        >
          Create
        </Button>
      </Box>

      <Box>
        <DataGrid
          rows={data}
          columns={columns}
          getRowId={(row) => row.id}
          sx={{
            ...commonGridStyles,
            border: 'none !important',
            backgroundColor: 'var(--bg-panel)',
          }}
        />
      </Box>

      <CreateEditPaymentSettingModal
        open={isModalOpen}
        siteId={siteId}
        onClose={handleCloseModal}
        onSave={handleSaveSetting}
        initialData={editingSetting}
        isLoading={isLoading}
        error={error}
      />

      <ConfirmDeleteModal
        open={deleteModal.open}
        title={`payment setting ${deleteModal.title}`}
        onClose={() => setDeleteModal({ ...deleteModal, open: false })}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default PaymentSettingsTable;
